/**
 * Lens colour preview: repaints the frame's featured photo to match the lens
 * colour the shopper picked.
 *
 * Gated behind a feature key the merchant pastes into the theme settings. The
 * gate here only decides whether to offer the feature — the key travels in
 * every recolor request, and the backend is what actually has to verify it.
 * Anything shipped in a theme is readable by anyone who opens the page.
 *
 * Ported from the OTIS theme; selectors and state fields follow Broadcast.
 */
import { getRxState, lensRenderSlug, stripImageSizeParams } from '#rx/core';
import { recolorLensImage } from '#rx/api';

// Only the first gallery slide is recoloured — never the active one.
// Following the active slide meant that paging the gallery moved the recolour
// onto whichever photo was showing, left the previous slide holding a render
// that did not belong to it, and kept re-capturing originals until the cycle
// never settled. Thumbnails and every other slide stay untouched.
const GALLERY_SELECTOR = '.product__slides';
const SLIDE_SELECTOR = '.product__slide';

(function initRxRecolor() {
  if (window.__rxRecolorInit) return;

  const host = document.querySelector('[data-rx-recolor]');
  const featureKey = host?.dataset.rxRecolorKey || '';
  if (!featureKey) return; // feature off until the merchant enters the key

  window.__rxRecolorInit = true;

  const state = getRxState();
  // Original (non-recolored) featured image — the recolor source and the
  // restore target. srcset/sizes are kept so restore is lossless.
  let original = null;
  let setUrl = null;
  let reqId = 0;
  let debounceTimer = null;
  const urlCache = new Map(); // colour slug → generated image url (per page view)
  const generated = new Set(); // every url we ever set — never a recolor input

  // The service keys renders by colour and coating together, so both are read
  // off the chosen variant.
  const recolorSlug = (lens) => (lens ? lensRenderSlug(lens.finish, lens.color) : null);

  const gallery = () => document.querySelector(GALLERY_SELECTOR);
  const mainSlide = () => gallery()?.querySelector(SLIDE_SELECTOR) ?? null;
  const mainImg = () => mainSlide()?.querySelector('img') ?? null;

  // True when a mutation touched the slide we own, so paging the gallery and
  // lazy-loading the other slides cost us nothing.
  const inMainSlide = (node) => {
    const slide = mainSlide();
    return Boolean(slide && node instanceof Node && slide.contains(node));
  };

  // The recolor service downloads image_url itself, so it must be publicly
  // reachable — rewrite preview origins to the canonical shop domain.
  function publicImageUrl(src) {
    try {
      const url = new URL(src, location.href);
      const shop = window.Shopify?.shop;
      if (shop && url.pathname.startsWith('/cdn/')) return `https://${shop}${url.pathname}${url.search}`;
      return url.href;
    } catch {
      return src;
    }
  }

  // Only a Shopify-hosted product image counts as an original. Anything else
  // (our own renders included) must never become the recolor source, or each
  // result feeds the next request and the backend cache can never hit.
  function isShopImage(src) {
    try {
      return new URL(src, location.href).pathname.startsWith('/cdn/');
    } catch {
      return false;
    }
  }

  function captureOriginal(el) {
    const src = el.currentSrc || el.src;
    if (!src || src === setUrl || generated.has(src) || !isShopImage(src)) return;
    const normalized = stripImageSizeParams(publicImageUrl(src));
    if (original && normalized !== original.src) {
      // A different frame (sibling swap): its renders start over.
      urlCache.clear();
      setUrl = null;
    }
    original = {
      src: normalized,
      srcset: el.getAttribute('srcset') || '',
      sizes: el.getAttribute('sizes') || '',
    };
  }

  function restore() {
    const el = mainImg();
    if (!el || !original) return;
    el.src = original.src;
    if (original.srcset) el.setAttribute('srcset', original.srcset);
    if (original.sizes) el.setAttribute('sizes', original.sizes);
    setUrl = null;
  }

  // Fetch the image off-screen first; resolves false on error or timeout, so an
  // unreachable CDN can never blank or hang the visible photo.
  function preloadImage(url, timeoutMs = 15000) {
    return new Promise((resolve) => {
      const probe = new Image();
      const timer = setTimeout(() => {
        probe.src = '';
        resolve(false);
      }, timeoutMs);
      probe.onload = () => {
        clearTimeout(timer);
        resolve(true);
      };
      probe.onerror = () => {
        clearTimeout(timer);
        resolve(false);
      };
      probe.src = url;
    });
  }

  async function apply() {
    const el = mainImg();
    if (!el) return;
    if (!original) captureOriginal(el);
    if (!original) return;

    const slug = recolorSlug(state.lensVariant);
    // A lens the service cannot name stays on the original photo.
    if (!slug) return void restore();
    const my = ++reqId;
    el.classList.add('rx-recolor-loading');
    try {
      // Always recolor from the original source, never from a recolored image.
      let url = urlCache.get(slug);
      if (!url) {
        const data = await recolorLensImage(original.src, slug, featureKey);
        if (my !== reqId) return;
        url = data.url;
        urlCache.set(slug, url);
        generated.add(url);
      }

      // Swap only once the render actually loaded; otherwise stay on the
      // original (and forget the url so a later attempt can retry).
      const loaded = await preloadImage(url);
      if (my !== reqId) return;
      if (!loaded) {
        urlCache.delete(slug);
        restore();
        return;
      }

      setUrl = url;
      // Kill the responsive set or the browser keeps showing the original.
      el.srcset = '';
      el.removeAttribute('sizes');
      el.src = url;
    } catch (error) {
      if (my === reqId) restore();
      console.warn('rx: recolor failed', error);
    } finally {
      if (my === reqId) mainImg()?.classList.remove('rx-recolor-loading');
    }
  }

  const scheduleApply = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(apply, 200);
  };

  state.addEventListener('rx:change', (event) => {
    if (event.detail?.key === 'lensVariant') scheduleApply();
  });

  const first = mainImg();
  if (first) captureOriginal(first);

  // The gallery re-renders on slide and sibling changes: a src we did not set
  // means a new original — re-capture and re-apply the current colour.
  const galleryEl = gallery();
  if (galleryEl) {
    const normalize = (src) => stripImageSizeParams(publicImageUrl(src));
    new MutationObserver((records) => {
      // `class` is deliberately not observed: the theme toggles media--active
      // on every slide change, which is none of our business.
      const touchedUs = records.some(
        (record) =>
          inMainSlide(record.target) ||
          [...record.addedNodes].some((node) => inMainSlide(node) || node.contains?.(mainSlide()))
      );
      if (!touchedUs) return;

      const current = mainImg();
      if (!current) return;
      const src = current.currentSrc || current.src;
      if (!src || src === setUrl || generated.has(src)) return;

      if (normalize(src) === original?.src) {
        // The theme restored the original (e.g. re-applied srcset). Reassert
        // the recoloured image from memory — no new request.
        if (setUrl && state.lensVariant) {
          current.srcset = '';
          current.removeAttribute('sizes');
          current.src = setUrl;
        }
        return;
      }

      captureOriginal(current);
      if (state.lensVariant) scheduleApply();
    }).observe(galleryEl, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
  }

  if (state.lensVariant) scheduleApply();

  // Cache warming: quietly pre-generate every lens colour for this frame so
  // swatch clicks resolve instantly. Results only land in the backend cache.
  function warmCache() {
    if (!original) return;
    const dataEl = document.querySelector('[data-rx-data]');
    if (!dataEl) return;

    let colors = [];
    try {
      colors = (JSON.parse(dataEl.textContent).lenses ?? [])
        .map((lens) => recolorSlug(lens))
        .filter(Boolean);
    } catch {
      return;
    }

    const current = recolorSlug(state.lensVariant);
    const queue = [...new Set(colors)].filter((slug) => slug !== current && !urlCache.has(slug));

    const next = async () => {
      const slug = queue.shift();
      if (!slug) return;
      await recolorLensImage(original.src, slug, featureKey)
        .then((data) => {
          if (data?.url) {
            urlCache.set(slug, data.url);
            generated.add(data.url);
          }
        })
        .catch(() => {});
      return next();
    };
    // Two lanes keep the service load modest.
    next();
    next();
  }

  const idle = window.requestIdleCallback ?? ((fn) => setTimeout(fn, 1));
  setTimeout(() => idle(warmCache), 4000);
})();
